// src/pages/FollowUps.jsx
//
// The two follow-up triggers from claire_architecture.md, kept visually
// separate on purpose:
//  - Reactive (v_leads_needing_followup, migration 026): they engaged,
//    nobody's replied since. Urgent.
//  - Scheduled/cadence (v_leads_due_for_touch, migration 028): it's been
//    N days since the last outbound touch regardless of engagement. A
//    soft nudge, not urgent -- and excludes anyone already in the reactive
//    list so the same lead doesn't nag twice for two different reasons.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

function formatDateTime(v) {
  return v ? new Date(v).toLocaleString() : "—";
}

function daysAgo(v) {
  if (!v) return null;
  const ms = Date.now() - new Date(v).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function relativeDays(d) {
  if (d === null) return "";
  return d === 0 ? "today" : d === 1 ? "1 day ago" : `${d} days ago`;
}

function ReactiveRow({ r }) {
  const d = daysAgo(r.last_inbound_at);
  return (
    <Link to={`/leads/${r.lead_id}`} className="flex flex-col gap-1 p-4 hover:bg-gray-50">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{r.name}</span>
        <span className="text-xs text-gray-500">{r.email || "no email on file"}</span>
        <span className="ml-auto text-xs text-gray-500">{relativeDays(d)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span className="uppercase font-medium">{r.platform}</span>
        <span>·</span>
        <span>{r.type}</span>
        <span>·</span>
        <span>status: {r.status}</span>
        <span>·</span>
        <span>score: {r.score ?? 0}</span>
        <span>·</span>
        <span>{formatDateTime(r.last_inbound_at)}</span>
      </div>
      {r.content ? <div className="mt-1 truncate text-sm text-gray-700">{r.content}</div> : null}
    </Link>
  );
}

function CadenceRow({ r }) {
  const overdueDays = daysAgo(r.next_touch_due_at);
  return (
    <Link to={`/leads/${r.lead_id}`} className="flex flex-col gap-1 p-4 hover:bg-gray-50">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{r.name}</span>
        <span className="text-xs text-gray-500">{r.email || "no email on file"}</span>
        <span className="ml-auto text-xs text-gray-500">
          due {overdueDays > 0 ? `${overdueDays}d ago` : "today"}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span>status: {r.status}</span>
        <span>·</span>
        <span>score: {r.score ?? 0}</span>
        <span>·</span>
        <span>last contacted {formatDateTime(r.last_contacted)}</span>
      </div>
    </Link>
  );
}

export default function FollowUps() {
  const [reactive, setReactive] = useState([]);
  const [cadence, setCadence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function fetchRows() {
    setLoading(true);
    setError("");
    const [reactiveRes, cadenceRes] = await Promise.all([
      supabase.from("v_leads_needing_followup").select("*"),
      supabase.from("v_leads_due_for_touch").select("*"),
    ]);
    if (reactiveRes.error) setError(reactiveRes.error.message);
    else setReactive(reactiveRes.data || []);
    if (cadenceRes.error) setError(cadenceRes.error.message);
    else setCadence(cadenceRes.data || []);
    setLoading(false);
  }

  useEffect(() => {
    fetchRows();
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Follow-ups</h1>
          <button
            onClick={fetchRows}
            className="rounded border bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>

        {error ? <div className="mt-4 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

        <h2 className="mt-6 text-sm font-semibold uppercase text-gray-500">Waiting on a reply</h2>
        <p className="mt-1 text-sm text-gray-600">
          Their last touchpoint came from them, not Claire. Sorted oldest first.
        </p>
        <div className="mt-2 rounded bg-white shadow">
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading…</div>
          ) : reactive.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">Nothing waiting on a reply right now.</div>
          ) : (
            <div className="flex flex-col divide-y">
              {reactive.map((r) => (
                <ReactiveRow key={r.lead_id} r={r} />
              ))}
            </div>
          )}
        </div>

        <h2 className="mt-6 text-sm font-semibold uppercase text-gray-500">Due for a check-in</h2>
        <p className="mt-1 text-sm text-gray-600">
          It's been past their follow-up window (default 7 days) since the last outbound touch,
          regardless of whether they engaged. A soft nudge, not urgent.
        </p>
        <div className="mt-2 rounded bg-white shadow">
          {loading ? (
            <div className="p-6 text-sm text-gray-500">Loading…</div>
          ) : cadence.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">No one's overdue for a check-in.</div>
          ) : (
            <div className="flex flex-col divide-y">
              {cadence.map((r) => (
                <CadenceRow key={r.lead_id} r={r} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
